import {
  Badge,
  Box,
  Button,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Popover,
  Typography,
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import EventIcon from '@mui/icons-material/Event';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import BuildIcon from '@mui/icons-material/Build';
import SearchIcon from '@mui/icons-material/Search';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import { useState, type ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Tooltip from '@mui/material/Tooltip';
import { useMyShiftEndConfirmation } from '@/hooks/useShiftEndConfirmation';
import {
  staffNotificationMessageKey,
  staffNotificationParams,
  useStaffNotifications,
  type StaffNotification,
} from '@/hooks/useStaffNotifications';

function notificationIcon(type: string): ReactElement {
  switch (type) {
    case 'vehicle_dropoff':
      return <DirectionsCarIcon color="primary" />;
    case 'booking':
      return <EventIcon color="primary" />;
    case 'approval':
      return <CheckCircleOutlineIcon color="warning" />;
    case 'work_order':
      return <BuildIcon color="info" />;
    case 'message':
      return <ChatBubbleOutlineIcon color="success" />;
    case 'inspection':
      return <SearchIcon color="secondary" />;
    default:
      return <InfoOutlinedIcon color="action" />;
  }
}

function formatTimeAgo(
  dateStr: string,
  t: (key: string, opts?: Record<string, unknown>) => string,
): string {
  const now = Date.now();
  const diff = now - new Date(dateStr).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return t('timeAgo.justNow');
  if (minutes < 60) return t('timeAgo.minutesAgo', { count: minutes });
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return t('timeAgo.hoursAgo', { count: hours });
  const days = Math.floor(hours / 24);
  return t('timeAgo.daysAgo', { count: days });
}

export function NotificationsMenu() {
  const { t } = useTranslation('common');
  const { t: th } = useTranslation('hr');
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const { notifications, unreadCount, markAsRead, markAllAsRead } = useStaffNotifications();
  const { data: shiftEndConfirmation } = useMyShiftEndConfirmation();
  const autoClockOutAlert =
    shiftEndConfirmation?.status === 'auto_closed' &&
    shiftEndConfirmation.needsEmployeeExplanation;

  const badgeCount = unreadCount + (autoClockOutAlert ? 1 : 0);
  const isEmpty = notifications.length === 0 && !autoClockOutAlert;

  const closeMenu = () => setAnchorEl(null);

  const handleItemClick = (notif: StaffNotification) => {
    if (!notif.readAt) {
      markAsRead(notif.id);
    }
    if (notif.deepLink) {
      closeMenu();
      // An absolute https link points to another environment, e.g. the test
      // environment's release page (#3478); it opens in a new tab.
      if (/^https:\/\//i.test(notif.deepLink)) {
        window.open(notif.deepLink, '_blank', 'noopener,noreferrer');
      } else {
        navigate(notif.deepLink);
      }
    }
  };

  return (
    <>
      <Tooltip title={t('notifications')}>
        <IconButton
          sx={{ ml: 1 }}
          aria-label={t('notifications')}
          onClick={(e) => setAnchorEl(e.currentTarget)}
          aria-haspopup="true"
          aria-expanded={Boolean(anchorEl)}
          data-testid="notifications-bell"
        >
          <Badge badgeContent={badgeCount} color="error">
            <NotificationsIcon />
          </Badge>
        </IconButton>
      </Tooltip>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={closeMenu}
        disableScrollLock
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        slotProps={{
          paper: {
            sx: {
              width: { xs: 'calc(100vw - 32px)', sm: 360 },
              maxHeight: { xs: 'calc(100vh - 160px)', sm: 480 },
              mt: 1,
            },
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.5 }}>
          <Typography variant="subtitle1" fontWeight={600}>
            {t('notifications')}
          </Typography>
          {unreadCount > 0 && (
            <Button
              size="small"
              startIcon={<DoneAllIcon />}
              onClick={() => markAllAsRead()}
            >
              {t('markAllRead')}
            </Button>
          )}
        </Box>
        <Divider />
        {autoClockOutAlert && (
          <ListItem
            sx={{ cursor: 'pointer', bgcolor: 'error.light' }}
            data-testid="auto-clock-out-alert"
            onClick={() => {
              closeMenu();
              window.dispatchEvent(new Event('open-auto-clock-out'));
            }}
          >
            <ListItemAvatar sx={{ minWidth: 40 }}><NotificationsIcon color="error" /></ListItemAvatar>
            <ListItemText
              primary={th('shiftEndPrompt.autoClosedTitle')}
              secondary={th('shiftEndPrompt.alertBody')}
            />
          </ListItem>
        )}
        {isEmpty ? (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <NotificationsIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              {t('noNotifications')}
            </Typography>
          </Box>
        ) : (
          <List disablePadding sx={{ overflow: 'auto' }}>
            {notifications.map((notif) => {
              const read = Boolean(notif.readAt);
              const params = staffNotificationParams(notif.params);
              return (
                <ListItem
                  key={notif.id}
                  alignItems="flex-start"
                  data-testid={`notification-item-${notif.id}`}
                  sx={{
                    cursor: notif.deepLink ? 'pointer' : 'default',
                    bgcolor: read ? 'transparent' : 'action.hover',
                    '&:hover': { bgcolor: 'action.selected' },
                  }}
                  onClick={() => handleItemClick(notif)}
                >
                  <ListItemAvatar sx={{ minWidth: 40, mt: 0.5 }}>
                    {notificationIcon(notif.type)}
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Typography variant="body2" fontWeight={read ? 400 : 600}>
                        {t(staffNotificationMessageKey(notif.titleKey), params)}
                      </Typography>
                    }
                    secondary={
                      <>
                        {notif.bodyKey ? (
                          <Typography variant="caption" component="span" display="block" color="text.secondary">
                            {t(staffNotificationMessageKey(notif.bodyKey), params)}
                          </Typography>
                        ) : null}
                        <Typography variant="caption" component="span" color="text.disabled">
                          {formatTimeAgo(notif.createdAt, t)}
                        </Typography>
                      </>
                    }
                  />
                </ListItem>
              );
            })}
          </List>
        )}
      </Popover>
    </>
  );
}
